<!-- 
Panel for displaying fees for a levy collection.
-->
<template>
  <v-card flat>
    <v-card-text class="py-0 pr-2">
      <our-busy-indicator :show="state.loading"/>
      <our-data-container css="pb-0">
        <our-data-field label="Administrative fee per provider">
          {{ configuration.administrationFeePerProvider | currency }}
          <up-down 
            v-if="forSandbox" 
            :original="original.administrationFeePerProvider" 
            :current="configuration.administrationFeePerProvider"
            small
          />
        </our-data-field>
        <our-data-field label="Administrative fee per student">
          {{ configuration.administrationFeePerStudent | currency }}
          <up-down 
            v-if="forSandbox" 
            :original="original.administrationFeePerStudent" 
            :current="configuration.administrationFeePerStudent"
            small
          />
        </our-data-field>
        <our-data-field label="Risk premium per student">
          {{ configuration.riskPremiumPerStudent | currency }}
          <up-down 
            v-if="forSandbox" 
            :original="original.riskPremiumPerStudent" 
            :current="configuration.riskPremiumPerStudent"
            small
          />
        </our-data-field>
        <our-data-field label="Risk premium percentage">
          {{ configuration.riskPremiumRate | percentage }}
          <up-down 
            v-if="forSandbox" 
            :original="original.riskPremiumRate" 
            :current="configuration.riskPremiumRate"
            is-percentage
            small
          />
        </our-data-field>
        <our-data-field label="Special tuition protection percentage">
          {{ configuration.specialTuitionProtectionRate | percentage }}
          <up-down 
            v-if="forSandbox" 
            :original="original.specialTuitionProtectionRate" 
            :current="configuration.specialTuitionProtectionRate"
            is-percentage
            small
          />
        </our-data-field>
        <our-data-field label="Levy cap" empty="No levy cap">
          {{ configuration.levyCap | percentage }}
          <up-down 
            v-if="forSandbox" 
            :original="original.levyCap" 
            :current="configuration.levyCap"
            is-percentage
            small
          />
        </our-data-field>
      </our-data-container>
    </v-card-text>
    <v-card-actions v-if="canEdit" class="justify-center">
      <v-btn class="edit-fees-button" @click="showEditDialog=true">
        <v-icon class="mr-2">edit</v-icon>
        Edit
      </v-btn>
      <fees-dialog 
        v-model="showEditDialog" 
        :levy-collection-id="levyCollectionId"
      />
    </v-card-actions>
  </v-card>
</template>

<script>
import FeesDialog from "@/components/levy-collection-setup/FeesDialog.vue";
import UpDown from "@/components/sandboxes/UpDown.vue";
import * as claim from "@/store/claim-types";
import * as action from "@/store/levy-collections/action-types";
import lookupMixins from "@/mixins/lookup.mixins";
import filterMixins from "@/mixins/filter.mixins";

export default {
  components: { FeesDialog, UpDown },
  mixins: [lookupMixins, filterMixins],
  props: {
    levyCollectionId: {
      type: Number,
      required: true
    },
    forSandbox: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showEditDialog: false
    };
  },
  computed: {
    state() {
      return this.$store.state.levyCollections.viewFees;
    },
    original() {
      return this.$store.state.sandboxes.original.fees;
    },
    configuration() {
      return this.state.data;
    },
    loaded() {
      return this.state.loaded;
    },
    canEdit() {
      const status = this.configuration.status;
      const isAllowableStatus = this.doesLookupHaveAttribute(
        status,
        "CanEditRiskFactors",
        this.lookups.levyCollectionStatuses
      );
      const permission = this.forSandbox
        ? claim.LEVY_COLLECTION_SANDBOXES
        : claim.LEVY_COLLECTION_CONFIGURE_FEES_AND_RATES;
      const hasPermission = this.$store.getters.claims.includes(permission);
      return hasPermission && isAllowableStatus;
    }
  },
  watch: {
    levyCollectionId() {
      this.refresh();
    },
    loaded() {
      this.refresh();
    }
  },
  mounted() {
    this.refresh();
  },
  methods: {
    refresh() {
      this.$store.dispatch(action.VIEW_REFRESH, this.levyCollectionId);
      const needsReloaded =
        (!this.state.loaded && !this.state.loading) || this.state.levyCollectionId != this.levyCollectionId;
      if (needsReloaded) {
        this.$store.dispatch(action.VIEW_FEES, this.levyCollectionId);
      }
    }
  }
};
</script>

<style lang="scss">
.fees-table {
  border: 1px solid #f0f0f0;
}
</style>